import * as React from 'react'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Autosuggest from '../../form/autosuggest/Autosuggest'
import SelectField from '../../form/SelectField'
import TextField from '../../form/TextField'
import Button from '../..//ui/button'
import InformationWindow from '../../informationWindow/informationWindow'
import PremiumDialog from '../../informationWindow/premiumDialog'

import { Formik, Form, Field, FormikValues, FieldProps } from 'formik'

import { AddToBlacklist, BlacklistItem, MemberState } from '../../../store/member/memberTypings'
import { ProfileShort } from '../../../store/commonTypes'
import { RootState } from '../../../store/rootReducer'

import { createLocalizedPath } from '../../../utils/localizationHelpers'
import { addToBlacklist } from '../../../api/member'
import { isUserPro } from '../../../utils/isUserPro'
import { openPremiumDialog } from '../../../store/common/commonSlice'
import xorWith from 'lodash/xorWith'
import paths from '../../../paths'
import './blacklist.sass'
import { DispatchForMiddlewares } from '@reduxjs/toolkit/dist/tsHelpers'

const initialValues: AddToBlacklist = {
    reason: '',
    type: '24h',
    nick: null
}

interface Props {
    getBlockedUsers: () => void
}

const BlacklistForm: React.FunctionComponent<Props> = ({ getBlockedUsers }) => {
    const [t] = useTranslation()
    const history = useHistory()
    const dispatch = useDispatch()

    const { userBlacklist } = useSelector((state: RootState) => state.member)
    const member = useSelector<RootState, MemberState>((state) => state.member)

    const [dialogMessage, setDialogMessage] = useState('')
    const [isDialogOpen, setIsDialogOpen] = useState(false)

    const blockedProfiles = userBlacklist.map((item: BlacklistItem) => item.banned_user)

    const filterSuggestions = (suggestions: ProfileShort[]) => {
        return xorWith(suggestions, blockedProfiles, (suggestion, blocked) => suggestion.nick === blocked.nick)
    }

    const typeOptions: FormikValues[] = [
        {
            value: '24h',
            label: t('24 hours')
        },
        {
            value: 'permanent',
            label: t('Permanent')
        }
    ]

    return (
        <>
            <Formik
                validateOnChange={false}
                initialValues={initialValues}
                onSubmit={(values: AddToBlacklist, formikHelpers): void => {
                    if (values.nick && isUserPro(member.user.member_type)) {
                        addToBlacklist(values)
                            .then(() => {
                                getBlockedUsers()
                            })
                            .catch((answer) => {
                                setDialogMessage(answer.messages)
                                setIsDialogOpen(true)
                            })
                        history.push({ pathname: createLocalizedPath(paths.myBlacklist.path) })
                    }
                    if (!isUserPro(member.user.member_type)) dispatch(openPremiumDialog())
                    formikHelpers.resetForm()
                }}
            >
                <Form className="form blacklistForm" data-testid={'blockForm'}>
                    <Field name="nick">
                        {({ field, form }: FieldProps) => (
                            <Autosuggest
                                form={form}
                                field={field}
                                label={t('User')}
                                placeholder={t('User nick')}
                                filterSuggestions={filterSuggestions}
                            />
                        )}
                    </Field>
                    <Field name="type">
                        {({ field, form }: FieldProps) => (
                            <SelectField form={form} field={field} label={t('Duration')} options={typeOptions} />
                        )}
                    </Field>
                    <Field name="reason">
                        {({ field, meta }: FieldProps) => (
                            <TextField
                                field={field}
                                type="text"
                                label={t('Reason')}
                                placeholder={t('Describe your reason')}
                                error={meta.touched && meta.error}
                            />
                        )}
                    </Field>
                    <Button type="submit" label={t('Add to blacklist')} />
                </Form>
            </Formik>
            <InformationWindow
                informationText={dialogMessage}
                setExternalState={setIsDialogOpen}
                externalState={isDialogOpen}
                labelFunctionalButton={t('Ok')}
            />
            <PremiumDialog />
        </>
    )
}

export default BlacklistForm
